import { DocumentWithCategoriesAndStatuses } from '@mercator-gcp/types/backend/Document';
import { atom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useQuery } from 'react-query';
import DocumentFiltersAPI from 'services/API/DocumentFiltersAPI';
import { renameDocuments } from 'utils/renameDocuments';
import type { Item } from 'types/misc';

const sortProjectStages = (projectStages: DocumentWithCategoriesAndStatuses[]) => {
  const referenceOrder = [
    'land title change',
    'land use redesignation',
    'entitlement',
    'plat',
    'development permit',
    'building permit',
    'electrical inspection',
    'tenancy change',
    'business license',
    'accessibility compliance',
  ];

  return projectStages.sort((a, b) => {
    // Convert labels to lowercase for case-insensitive comparison
    const indexA = referenceOrder.indexOf(a.name.toLowerCase());
    const indexB = referenceOrder.indexOf(b.name.toLowerCase());

    // If one label is not in the reference list, place its item at the end
    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }

    // If both labels are in the reference list, order their items by the labels' positions in the reference list
    return indexA - indexB;
  });
};

/* Migrating this file from jotai to react-query */

export const useEventTriggerStatuses = (cityIds: number[], documentTypeId: number[]) => {
  const { data, isLoading } = useQuery<Item[]>(
    ['eventTriggerStatuses', cityIds, documentTypeId],
    async () => {
      const response = await DocumentFiltersAPI.getDocumentFilterStatuses(
        cityIds,
        documentTypeId,
      );

      return response.data;
    },
    {
      enabled: !!cityIds && !!documentTypeId,
      refetchOnWindowFocus: false,
    },
  );

  return { data: data || [], isLoading };
};

export const useEventTriggerCategories = (
  cityIds: number[],
  documentTypeId: number[],
) => {
  const { data, isLoading } = useQuery<Item[]>(
    ['eventTriggerCategories', cityIds, documentTypeId],
    async () => {
      const response = await DocumentFiltersAPI.getDocumentFilterCategories(
        cityIds,
        documentTypeId,
      );

      return response.data;
    },
    {
      enabled: !!cityIds && !!documentTypeId,
      refetchOnWindowFocus: false,
    },
  );

  return { data: data || [], isLoading };
};
/**
 * CityIds is a string param. It cannot be a list of ids. Otherwise jotai
 * continuosly makes new requests.
 */
const useEventTriggerDocTypesByState = atomFamily((stateCode: string | null) =>
  atom(async () => {
    if (!stateCode) {
      return [];
    }

    const { data } = await DocumentFiltersAPI.getDocumentFilterTypesByState(stateCode);

    const sortedData = sortProjectStages(data as DocumentWithCategoriesAndStatuses[]);

    return sortedData.map((item) => ({
      ...item,
      name: renameDocuments(item.name),
    }));
  }),
);

export const useEventTriggerDocTypeByStateCode = (stateCode: string | null) => {
  return useAtomValue(useEventTriggerDocTypesByState(stateCode));
};

/* Migrating this file from jotai to react-query */

export const useEventTriggerStatusesByState = (
  stateCode: string | null,
  documentTypeId: number[],
) => {
  const { data, isLoading } = useQuery<Item[]>(
    ['eventTriggerStatuses', stateCode, documentTypeId],
    async () => {
      if (!stateCode) {
        return [];
      }
      const response = await DocumentFiltersAPI.getDocumentFilterStatusesByState(
        stateCode,
        documentTypeId,
      );

      return response.data;
    },
    {
      enabled: !!stateCode && !!documentTypeId,
      refetchOnWindowFocus: false,
    },
  );

  return { data: data || [], isLoading };
};

export const useEventTriggerCategoriesByState = (
  stateCode: string | null,
  documentTypeId: number[],
) => {
  const { data, isLoading } = useQuery<Item[]>(
    ['eventTriggerCategories', stateCode, documentTypeId],
    async () => {
      if (!stateCode) {
        return [];
      }
      const response = await DocumentFiltersAPI.getDocumentFilterCategoriesByState(
        stateCode,
        documentTypeId,
      );

      return response.data;
    },
    {
      enabled: !!stateCode && !!documentTypeId,
      refetchOnWindowFocus: false,
    },
  );

  return { data: data || [], isLoading };
};
